import { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Pill, Wine, Apple, Droplet, Printer, Layers } from 'lucide-react';

const Products = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-16 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_pslcote-labels/artifacts/kyaorlo8_ChatGPT%20Image%20Nov%2017%2C%202025%2C%2011_22_37%20AM.png" 
            alt="Label Stock Materials"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/30"></div>
        </div>
        <div className="section-container relative z-10">
          <h1 className="text-4xl lg:text-5xl font-bold text-white text-center mb-4 drop-shadow-lg" data-testid="products-page-title">Product Categories</h1>
          <p className="text-lg lg:text-xl text-white text-center mx-auto max-w-3xl drop-shadow-md">
            Specialized label stock solutions engineered for diverse industry requirements
          </p>
        </div>
      </section>

      {/* Products Tabs */}
      <section className="py-16 lg:py-24">
        <div className="section-container">
          <Tabs defaultValue="pharma" className="w-full">
            <TabsList className="grid w-full grid-cols-2 lg:grid-cols-6 gap-2 bg-transparent h-auto mb-12">
              <TabsTrigger 
                value="pharma" 
                className="data-[state=active]:bg-[#0066cc] data-[state=active]:text-white flex flex-col items-center gap-2 py-4 rounded-lg border-2 border-gray-200 data-[state=active]:border-[#0066cc]"
                data-testid="tab-pharma"
              >
                <Pill size={24} />
                <span className="text-xs lg:text-sm">Pharma & Healthcare</span>
              </TabsTrigger>
              <TabsTrigger 
                value="wine" 
                className="data-[state=active]:bg-[#0066cc] data-[state=active]:text-white flex flex-col items-center gap-2 py-4 rounded-lg border-2 border-gray-200 data-[state=active]:border-[#0066cc]"
                data-testid="tab-wine"
              >
                <Wine size={24} />
                <span className="text-xs lg:text-sm">Wine & Spirits</span>
              </TabsTrigger>
              <TabsTrigger 
                value="food" 
                className="data-[state=active]:bg-[#0066cc] data-[state=active]:text-white flex flex-col items-center gap-2 py-4 rounded-lg border-2 border-gray-200 data-[state=active]:border-[#0066cc]"
                data-testid="tab-food"
              >
                <Apple size={24} />
                <span className="text-xs lg:text-sm">Food & Beverages</span>
              </TabsTrigger>
              <TabsTrigger 
                value="oil" 
                className="data-[state=active]:bg-[#0066cc] data-[state=active]:text-white flex flex-col items-center gap-2 py-4 rounded-lg border-2 border-gray-200 data-[state=active]:border-[#0066cc]"
                data-testid="tab-oil"
              >
                <Droplet size={24} />
                <span className="text-xs lg:text-sm">Oil & Chemicals</span>
              </TabsTrigger>
              <TabsTrigger 
                value="digital" 
                className="data-[state=active]:bg-[#0066cc] data-[state=active]:text-white flex flex-col items-center gap-2 py-4 rounded-lg border-2 border-gray-200 data-[state=active]:border-[#0066cc]"
                data-testid="tab-digital"
              >
                <Printer size={24} />
                <span className="text-xs lg:text-sm">Digital Printing</span>
              </TabsTrigger>
              <TabsTrigger 
                value="materials" 
                className="data-[state=active]:bg-[#0066cc] data-[state=active]:text-white flex flex-col items-center gap-2 py-4 rounded-lg border-2 border-gray-200 data-[state=active]:border-[#0066cc]"
                data-testid="tab-materials"
              >
                <Layers size={24} />
                <span className="text-xs lg:text-sm">Materials</span>
              </TabsTrigger>
            </TabsList>

            {/* Pharma & Healthcare */}
            <TabsContent value="pharma" data-testid="content-pharma">
              <div className="bg-white rounded-xl shadow-lg p-8 lg:p-12">
                <h2 className="text-3xl font-bold mb-6 pslcote-blue">Pharma & Healthcare Labeling</h2>
                <p className="text-gray-700 leading-relaxed mb-8">
                  The pharmaceutical and healthcare sector demands absolute precision and reliability in labeling—where product safety, regulatory compliance, and traceability are non-negotiable. Labels must endure sterilization, refrigeration, cryogenic storage, and chemical exposure, while maintaining print clarity and adhesive integrity across the entire product lifecycle.
                </p>

                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  <div>
                    <h3 className="text-xl font-semibold mb-4">Label Challenges</h3>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">•</span> Extreme temperature exposure during cold chain logistics</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">•</span> Resistance to moisture, alcohol, and chemical sterilization</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">•</span> Tamper-evidence and security labeling for compliance</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">•</span> Compatibility with vials, ampoules, syringes</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">•</span> Barcode scanability under demanding conditions</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-4">PSLCOTE Solutions</h3>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">✓</span> Cryogenic adhesives resistant to -80°C</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">✓</span> Clean-release liners for high-speed application</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">✓</span> FDA and EU GMP regulatory compliance</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">✓</span> Sterilization-resistant materials</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">✓</span> Print permanence and barcode clarity</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold mb-4">Application Areas</h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                    <div className="bg-white p-4 rounded">
                      <p className="font-semibold text-[#0066cc] mb-2">Vials & Ampoules</p>
                      <p className="text-gray-600">PET Film + Cryo Adhesive</p>
                    </div>
                    <div className="bg-white p-4 rounded">
                      <p className="font-semibold text-[#0066cc] mb-2">Cryogenic Storage</p>
                      <p className="text-gray-600">Synthetic Paper + Cryo Adhesive</p>
                    </div>
                    <div className="bg-white p-4 rounded">
                      <p className="font-semibold text-[#0066cc] mb-2">Blister Packs</p>
                      <p className="text-gray-600">High Opacity Paper</p>
                    </div>
                    <div className="bg-white p-4 rounded">
                      <p className="font-semibold text-[#0066cc] mb-2">IV Bag Labels</p>
                      <p className="text-gray-600">PE Film + Autoclave-Resistant</p>
                    </div>
                    <div className="bg-white p-4 rounded">
                      <p className="font-semibold text-[#0066cc] mb-2">Security Labels</p>
                      <p className="text-gray-600">VOID / Destructible Film</p>
                    </div>
                    <div className="bg-white p-4 rounded">
                      <p className="font-semibold text-[#0066cc] mb-2">Sterile Bags</p>
                      <p className="text-gray-600">High-temp sterilization resistant</p>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Wine & Spirits */}
            <TabsContent value="wine" data-testid="content-wine">
              <div className="bg-white rounded-xl shadow-lg p-8 lg:p-12">
                <h2 className="text-3xl font-bold mb-6 pslcote-blue">Wine & Spirits Labeling</h2>
                <p className="text-gray-700 leading-relaxed mb-8">
                  In the world of fine wines and spirits, the label is more than just information—it's an expression of brand identity and craftsmanship. These products demand materials that convey luxury, authenticity, and durability, even in challenging serving and storage conditions.
                </p>

                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  <div>
                    <h3 className="text-xl font-semibold mb-4">Label Challenges</h3>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">•</span> Moisture exposure and ice-bucket immersion</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">•</span> Premium aesthetics with texture and sheen</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">•</span> Foil compatibility and consistent coloration</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">•</span> Adhesion to tight bottle curves and varied surfaces</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">•</span> Resistance to wrinkling and lifting in humidity</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-4">PSLCOTE Solutions</h3>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">✓</span> Textured, metallised, ultra-clear films</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">✓</span> Ice bucket resistance up to 4 hours</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">✓</span> High-end decoration support (embossing, foil)</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">✓</span> Water and ice-resistant adhesives</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">✓</span> Temperature range -5°C to 40°C</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold mb-4">Application Areas</h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                    <div className="bg-white p-4 rounded">
                      <p className="font-semibold text-[#0066cc] mb-2">Wine Bottles</p>
                      <p className="text-gray-600">Textured Paper + Ice-Resistant</p>
                    </div>
                    <div className="bg-white p-4 rounded">
                      <p className="font-semibold text-[#0066cc] mb-2">Whisky/Rum Bottles</p>
                      <p className="text-gray-600">Metallised Paper Premium</p>
                    </div>
                    <div className="bg-white p-4 rounded">
                      <p className="font-semibold text-[#0066cc] mb-2">Vodka/Gin</p>
                      <p className="text-gray-600">Ultra-Clear Film No-label Look</p>
                    </div>
                    <div className="bg-white p-4 rounded">
                      <p className="font-semibold text-[#0066cc] mb-2">Champagne</p>
                      <p className="text-gray-600">Moisture Barrier Adhesive</p>
                    </div>
                    <div className="bg-white p-4 rounded">
                      <p className="font-semibold text-[#0066cc] mb-2">Limited Editions</p>
                      <p className="text-gray-600">Foil & Embossing Compatible</p>
                    </div>
                    <div className="bg-white p-4 rounded">
                      <p className="font-semibold text-[#0066cc] mb-2">Gift Packs</p>
                      <p className="text-gray-600">Premium Natural Finish</p>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Food & Beverages */}
            <TabsContent value="food" data-testid="content-food">
              <div className="bg-white rounded-xl shadow-lg p-8 lg:p-12">
                <h2 className="text-3xl font-bold mb-6 pslcote-blue">Food & Beverages Labeling</h2>
                <p className="text-gray-700 leading-relaxed mb-8">
                  The food and beverages industry is one of the most diverse and visually driven labeling segments, where appearance, hygiene, and durability define consumer appeal. Labels must not only elevate brand presentation but also maintain adhesion and legibility under extreme storage and usage conditions.
                </p>

                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  <div>
                    <h3 className="text-xl font-semibold mb-4">Label Challenges</h3>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">•</span> Multi-substrate adhesion (glass, PET, metal, board)</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">•</span> Hot filling, refrigeration, deep-freeze conditions</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">•</span> Print brilliance and scuff resistance</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">•</span> Oil, grease, and moisture resistance</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">•</span> Food-grade adhesives for regulatory compliance</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-4">PSLCOTE Solutions</h3>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">✓</span> Multi-surface adhesion technology</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">✓</span> Temperature-resistant materials</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">✓</span> Print-ready coatings for vivid graphics</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">✓</span> Food-safety compliant materials</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">✓</span> High-speed application optimized</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold mb-4">Application Areas</h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                    <div className="bg-white p-4 rounded">
                      <p className="font-semibold text-[#0066cc] mb-2">Bottled Water</p>
                      <p className="text-gray-600">Clear BOPP Film No-label Look</p>
                    </div>
                    <div className="bg-white p-4 rounded">
                      <p className="font-semibold text-[#0066cc] mb-2">Sauces & Jams</p>
                      <p className="text-gray-600">Coated Paper Gloss Finish</p>
                    </div>
                    <div className="bg-white p-4 rounded">
                      <p className="font-semibold text-[#0066cc] mb-2">Frozen Foods</p>
                      <p className="text-gray-600">PE Film Low-Temperature</p>
                    </div>
                    <div className="bg-white p-4 rounded">
                      <p className="font-semibold text-[#0066cc] mb-2">Canned Goods</p>
                      <p className="text-gray-600">Metallised High-Tack</p>
                    </div>
                    <div className="bg-white p-4 rounded">
                      <p className="font-semibold text-[#0066cc] mb-2">Snack Packaging</p>
                      <p className="text-gray-600">Textured Paper Natural Feel</p>
                    </div>
                    <div className="bg-white p-4 rounded">
                      <p className="font-semibold text-[#0066cc] mb-2">Soft Drinks</p>
                      <p className="text-gray-600">Condensation-resistant</p>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Oil & Industrial Chemicals */}
            <TabsContent value="oil" data-testid="content-oil">
              <div className="bg-white rounded-xl shadow-lg p-8 lg:p-12">
                <h2 className="text-3xl font-bold mb-6 pslcote-blue">Oil & Industrial Chemical Labeling</h2>
                <p className="text-gray-700 leading-relaxed mb-8">
                  The oil, lubricant, and industrial chemical segment demands exceptionally rugged label stock capable of withstanding the harshest operating conditions. Labels must perform under extreme temperatures, chemical exposure, abrasion, and moisture while maintaining readability and adhesion.
                </p>

                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  <div>
                    <h3 className="text-xl font-semibold mb-4">Label Challenges</h3>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">•</span> Exposure to oils, solvents, aggressive chemicals</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">•</span> Low-surface-energy substrates (HDPE, metal)</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">•</span> Physical abrasion during transport</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">•</span> Temperature variations and outdoor storage</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">•</span> Hazard communication and GHS compliance</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-4">PSLCOTE Solutions</h3>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">✓</span> Chemical and solvent-resistant materials</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">✓</span> High-tack adhesives for HDPE surfaces</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">✓</span> UV-resistant for outdoor exposure</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">✓</span> Abrasion-resistant top coatings</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">✓</span> GHS/CLP standards compliant</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold mb-4">Application Areas</h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                    <div className="bg-white p-4 rounded">
                      <p className="font-semibold text-[#0066cc] mb-2">Lubricant Cans</p>
                      <p className="text-gray-600">PP Film Chemical-Resistant</p>
                    </div>
                    <div className="bg-white p-4 rounded">
                      <p className="font-semibold text-[#0066cc] mb-2">Chemical Drums</p>
                      <p className="text-gray-600">PET Film High-Tack</p>
                    </div>
                    <div className="bg-white p-4 rounded">
                      <p className="font-semibold text-[#0066cc] mb-2">Solvent Containers</p>
                      <p className="text-gray-600">Metallised UV Resistant</p>
                    </div>
                    <div className="bg-white p-4 rounded">
                      <p className="font-semibold text-[#0066cc] mb-2">Export Drums</p>
                      <p className="text-gray-600">PE Film Heavy-Duty</p>
                    </div>
                    <div className="bg-white p-4 rounded">
                      <p className="font-semibold text-[#0066cc] mb-2">Hazard Labels</p>
                      <p className="text-gray-600">Synthetic Tear-Proof</p>
                    </div>
                    <div className="bg-white p-4 rounded">
                      <p className="font-semibold text-[#0066cc] mb-2">Oil Containers</p>
                      <p className="text-gray-600">Grease-resistant</p>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Digital Printing */}
            <TabsContent value="digital" data-testid="content-digital">
              <div className="bg-white rounded-xl shadow-lg p-8 lg:p-12">
                <h2 className="text-3xl font-bold mb-6 pslcote-blue">Digital Printing Labels</h2>
                <p className="text-gray-700 leading-relaxed mb-8">
                  As brands move toward short-run, high-quality, and personalized packaging, digital printing has become a key driver of transformation. Converters and brand owners demand label stock that delivers sharp imaging, vivid color reproduction, and consistent print performance across various digital technologies.
                </p>

                <div className="grid md:grid-cols-2 gap-8 mb-8">
                  <div>
                    <h3 className="text-xl font-semibold mb-4">Label Challenges</h3>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">•</span> Multi-platform compatibility (HP Indigo, Inkjet, Laser)</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">•</span> Consistent print finish across conditions</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">•</span> Vivid color reproduction without bleeding</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">•</span> Toner adhesion on varied substrates</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">•</span> Static control and feeding issues</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold mb-4">PSLCOTE Solutions</h3>
                    <ul className="space-y-2 text-gray-600">
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">✓</span> Multi-platform engineered coatings</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">✓</span> Enhanced toner anchorage technology</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">✓</span> Anti-static treatments for reliable feeding</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">✓</span> Curl-resistant materials</li>
                      <li className="flex items-start gap-2"><span className="text-[#0066cc] mt-1">✓</span> Fast-drying formulations</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-xl font-semibold mb-4">Application Areas</h3>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                    <div className="bg-white p-4 rounded">
                      <p className="font-semibold text-[#0066cc] mb-2">FMCG Labels</p>
                      <p className="text-gray-600">Gloss White Digital Coating</p>
                    </div>
                    <div className="bg-white p-4 rounded">
                      <p className="font-semibold text-[#0066cc] mb-2">Beverage Personalization</p>
                      <p className="text-gray-600">Clear Film Inkjet Compatible</p>
                    </div>
                    <div className="bg-white p-4 rounded">
                      <p className="font-semibold text-[#0066cc] mb-2">Promotional Labels</p>
                      <p className="text-gray-600">Matte Paper Laser Coating</p>
                    </div>
                    <div className="bg-white p-4 rounded">
                      <p className="font-semibold text-[#0066cc] mb-2">Industrial Labels</p>
                      <p className="text-gray-600">PET Film UV Inkjet</p>
                    </div>
                    <div className="bg-white p-4 rounded">
                      <p className="font-semibold text-[#0066cc] mb-2">Variable Data</p>
                      <p className="text-gray-600">Synthetic Thermal Transfer</p>
                    </div>
                    <div className="bg-white p-4 rounded">
                      <p className="font-semibold text-[#0066cc] mb-2">Barcode Labels</p>
                      <p className="text-gray-600">High-resolution print ready</p>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Materials & Components */}
            <TabsContent value="materials" data-testid="content-materials">
              <div className="bg-white rounded-xl shadow-lg p-8 lg:p-12">
                <h2 className="text-3xl font-bold mb-6 pslcote-blue">Label Stock Materials & Components</h2>
                <p className="text-gray-700 leading-relaxed mb-8">
                  Comprehensive material selection providing complete flexibility for custom label stock engineering
                </p>

                <div className="space-y-8">
                  {/* Face Materials */}
                  <div>
                    <h3 className="text-2xl font-semibold mb-4 text-[#0066cc]">Face Materials</h3>
                    
                    <div className="mb-6">
                      <h4 className="text-lg font-semibold mb-3">Paper Materials</h4>
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                        <div className="bg-gray-50 p-3 rounded">Semi Gloss/Chromo (Various GSM)</div>
                        <div className="bg-gray-50 p-3 rounded">Pharma Paper</div>
                        <div className="bg-gray-50 p-3 rounded">Cast Coated/Mirror Coat</div>
                        <div className="bg-gray-50 p-3 rounded">Vellum</div>
                        <div className="bg-gray-50 p-3 rounded">Maplitho</div>
                        <div className="bg-gray-50 p-3 rounded">Thermal Transfer Paper</div>
                        <div className="bg-gray-50 p-3 rounded">Direct Thermal Papers</div>
                        <div className="bg-gray-50 p-3 rounded">Fluorescent (Various Colors)</div>
                        <div className="bg-gray-50 p-3 rounded">Metallised & Aluminium Foil</div>
                        <div className="bg-gray-50 p-3 rounded">Wet Strength Paper</div>
                        <div className="bg-gray-50 p-3 rounded">Multi-Layered Materials</div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-lg font-semibold mb-3">Film Materials</h4>
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                        <div className="bg-gray-50 p-3 rounded">Polypropylene (Clear, Opaque, Matte)</div>
                        <div className="bg-gray-50 p-3 rounded">Polyester (Clear, Opaque)</div>
                        <div className="bg-gray-50 p-3 rounded">Polyester Metallised</div>
                        <div className="bg-gray-50 p-3 rounded">Polyethylene (Clear, Opaque)</div>
                      </div>
                    </div>
                  </div>

                  {/* Adhesives */}
                  <div>
                    <h3 className="text-2xl font-semibold mb-4 text-[#0066cc]">Pressure Sensitive Adhesives</h3>
                    
                    <div className="grid md:grid-cols-3 gap-6">
                      <div>
                        <h4 className="text-lg font-semibold mb-3">Rubber Based</h4>
                        <ul className="space-y-2 text-sm text-gray-600">
                          <li>• Permanent</li>
                          <li>• Removable</li>
                          <li>• Semi Freezer</li>
                          <li>• Deep Freeze</li>
                          <li>• Direct Food</li>
                          <li>• Peelable</li>
                        </ul>
                      </div>

                      <div>
                        <h4 className="text-lg font-semibold mb-3">Hot Melt</h4>
                        <ul className="space-y-2 text-sm text-gray-600">
                          <li>• Permanent</li>
                          <li>• Removable</li>
                          <li>• Semi Freezer</li>
                          <li>• Deep Freeze</li>
                          <li>• Direct Food</li>
                          <li>• Peelable</li>
                        </ul>
                      </div>

                      <div>
                        <h4 className="text-lg font-semibold mb-3">Water Based Acrylic</h4>
                        <ul className="space-y-2 text-sm text-gray-600">
                          <li>• Permanent</li>
                          <li>• Removable</li>
                          <li>• Semi Freezer</li>
                          <li>• Deep Freeze</li>
                          <li>• Direct Food</li>
                          <li>• Peelable</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Release Liners */}
                  <div>
                    <h3 className="text-2xl font-semibold mb-4 text-[#0066cc]">Release Liners</h3>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                      <div className="bg-gray-50 p-4 rounded">
                        <p className="font-semibold mb-2">Glassine</p>
                        <p className="text-gray-600 text-xs">Various GSM & Colors</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded">
                        <p className="font-semibold mb-2">CCK</p>
                        <p className="text-gray-600 text-xs">Clay Coated Kraft</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded">
                        <p className="font-semibold mb-2">SCK</p>
                        <p className="text-gray-600 text-xs">Super Calendared Kraft</p>
                      </div>
                      <div className="bg-gray-50 p-4 rounded">
                        <p className="font-semibold mb-2">PP Films</p>
                        <p className="text-gray-600 text-xs">Polypropylene Films</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
};

export default Products;